// Emily Burns · Portfolio Translations (EN / FR / ES)
// Kept in a single file for easy editing by the player’s representation.

const en = {
  nav: {
    profile: "Profile",
    video: "Video",
    tactical: "Tactical",
    stats: "Stats",
    career: "Career",
    achievements: "Honours",
    gallery: "Gallery",
    contact: "Contact",
    dossier: "Dossier",
    downloadDossier: "Download Dossier"
  },
  hero: {
    eyebrow: "Scouting Profile · 2025/26",
    subheadline:
      "Goalkeeper at Olympique de Marseille. Commanding the box. Owning the moment. Built for the biggest stages.",
    watchReel: "Watch Highlight Reel",
    viewProfile: "View Scouting Profile",
    scroll: "Scroll",
    position: "Position",
    nationality: "Nationality",
    height: "Height",
    currentClub: "Current Club"
  },
  profile: {
    eyebrow: "01 · Scouting Profile",
    title: "The ",
    titleHighlight: "Player",
    summary:
      "Emily Burns is a modern goalkeeper with a strong shot-stopping profile, high efficiency in goal prevention, and a reliable presence in defensive situations. She combines excellent reflexes with strong positional awareness, making her a consistent performer in high-pressure scenarios.",
    labels: {
      dob: "Date of Birth",
      nationality: "Nationality",
      height: "Height",
      foot: "Preferred Foot",
      club: "Current Club",
      languages: "Languages",
      contract: "Contract Until",
      status: "Status"
    },
    values: {
      dob: "24 July 1997 · 28 yrs",
      foot: "Right",
      club: "Olympique de Marseille · France",
      languages: "English, French, Spanish",
      contract: "June 2026",
      status: ""
    }
  },
  video: {
    eyebrow: "02 · Video",
    title: "On the ",
    titleHighlight: "Pitch",
    description: "Official scouting reels — shared privately for secure club distribution.",
    featured: "Featured · Long Version",
    featuredTitle: "Official Highlight Reel · Long Version",
    featuredDescription:
      "Extended scouting reel — saves, distribution, 1v1s and match footage.",
    shortTitle: "Highlight Reel · Short Version",
    shortDuration: "Short cut",
    fullDuration: "Full reel",
    noteTitle: "Scouting Note",
    noteText:
      "Two versions available for convenience: an extended reel for a complete technical evaluation, and a short cut for quick review.",
    nowPlaying: "Now Playing",
    tags: ["Saves", "Distribution", "1v1", "Aerial", "Match footage"]
  },
  tactical: {
    eyebrow: "Scouting Report · Tactical",
    title: "Tactical",
    titleHighlight: "Profile",
    intro:
      "Emily Burns is a modern goalkeeper with a strong shot-stopping profile, high efficiency in goal prevention, and a reliable presence in defensive situations. She combines excellent reflexes with strong positional awareness, making her a consistent performer in high-pressure scenarios.",
    blocks: [
      {
        tag: "01",
        title: "Style of Play",
        paragraphs: [
          "Emily is a reactive and efficient goalkeeper who excels in situations requiring fast decision-making and immediate response. Her game is built around shot-stopping, positioning, and reading danger early."
        ]
      },
      {
        tag: "02",
        title: "Shot-Stopping & Reflexes",
        paragraphs: [
          "One of her standout strengths. Emily shows exceptional reaction speed, particularly in close-range and unpredictable situations. She is highly effective against low-driven shots and deflections, consistently making saves thanks to her reflexes and body coordination.",
          "Her save efficiency ranks above average across multiple top leagues, and she performs above 75% of goalkeepers in saves per 90 minutes in high-level competitions."
        ]
      },
      {
        tag: "03",
        title: "Goals Prevented Impact",
        paragraphs: [
          "Emily is a high-impact goalkeeper in terms of goals prevented. She consistently performs above average in goals prevented per 90 minutes, demonstrating her ability to influence match outcomes directly.",
          "Her interventions have been decisive, contributing significantly to team results, including key saves in decisive moments."
        ]
      },
      {
        tag: "04",
        title: "Coming Off The Line",
        paragraphs: [
          "She shows very good timing when leaving her line, attacking the ball with determination and effectively reducing the attacker’s available space. Her ability to extend her body and react quickly allows her to win 1v1 situations."
        ]
      },
      {
        tag: "05",
        title: "Aerial & Box Control",
        paragraphs: [
          "Emily demonstrates solid performance in aerial duels, with a success rate of 72%, showing reliability in crosses and set-piece situations."
        ]
      },
      {
        tag: "06",
        title: "Distribution & Build-up",
        paragraphs: [
          "Her footwork is technically reliable and decision-making under pressure is strong. She rarely misplaces passes and shows good awareness before receiving the ball."
        ]
      },
      {
        tag: "07",
        title: "Mentality & Consistency",
        paragraphs: [
          "Emily has proven to be a reliable and consistent performer, capable of sustaining a high level over time. She has delivered crucial saves in key moments, including late-game interventions that have directly contributed to points for her team.",
          "She plays with composure, focus, and strong game understanding, maintaining performance under pressure."
        ]
      }
    ],
    competitive: {
      title: "Competitive Level",
      note:
        "She is currently at an optimal stage of maturity for the position, combining experience with continued development potential.",
      bullets: [
        "Top 10 goalkeeper profile in high-level competitions",
        "Above average across key performance metrics",
        "Competes favorably against goalkeepers in WSL, Liga F, NWSL and Bundesliga contexts"
      ]
    },
    summary: {
      label: "Profile Summary",
      p1:
        "A high-performing goalkeeper with elite shot-stopping ability, strong impact in goal prevention, and solid defensive presence. Particularly suited for teams that require reliability under pressure and a goalkeeper capable of securing results through decisive saves.",
      p2:
        "Her profile fits well in competitive environments and teams that prioritize defensive solidity, while still offering room for growth in possession-based systems."
    }
  },
  stats: {
    eyebrow: "03 · Performance",
    title: "Numbers ",
    titleHighlight: "Speak",
    season: "2025/26 · Arkema Première Ligue",
    labels: {
      matches: "Matches Played",
      minutes: "Minutes",
      saves: "Saves %",
      aerials: "Aerial Duels Won",
      reflex: "Reflex Saves",
      passAcc: "Pass Accuracy",
      longPass: "Long Pass Accuracy",
      gPrevented: "Goals Prevented / 90",
      totalActions: "Total Actions",
      cleanSheets: "Clean Sheets"
    },
    rankingsTitle: "League Benchmarking · Wyscout",
    rankingsNote: "Comparative goalkeeper ranking across top leagues and worldwide."
  },
  career: {
    eyebrow: "04 · Career",
    title: "The ",
    titleHighlight: "Journey",
    intro: "Senior career across France, Spain and Canada.",
    matchesLabel: "Matches",
    leagueLabel: "League"
  },
  achievements: {
    eyebrow: "05 · Honours",
    title: "Awards & ",
    titleHighlight: "Impact",
    intro: "Recognition and decisive contributions across recent seasons."
  },
  gallery: {
    eyebrow: "06 · Gallery",
    title: "In ",
    titleHighlight: "Frame"
  },
  dossier: {
    eyebrow: "07 · Dossier",
    title: "The ",
    titleHighlight: "Full File",
    intro:
      "A complete, club-ready PDF scouting dossier compiled for sporting directors, scouts and analysts. Includes full statistical profile, tactical analysis, league benchmarking and career record.",
    download: "Download PDF Dossier",
    request: "Request private access",
    confidential: "Confidential · Distributed on request",
    includedTitle: "What's Included",
    included: [
      "Full scouting profile & biography",
      "Statistical profile with Wyscout data",
      "Tactical & technical attributes",
      "League benchmarking (ENG, ESP, USA, World Top 20)",
      "Career record · last 6 seasons",
      "Honours & decisive contributions"
    ],
    format: "Format",
    updated: "Updated",
    updatedValue: "2025/26"
  },
  contact: {
    eyebrow: "08 · Contact",
    title: "Direct",
    titleHighlight: "Enquiries",
    intro:
      "For transfer enquiries, scouting requests or additional information — all messages are handled directly by official FIFA-licensed representation.",
    email: "Email",
    phone: "Phone / WhatsApp",
    representation: "Representation",
    basedIn: "Based In",
    instagram: "Instagram",
    fullName: "Your Name",
    club: "Club / Organisation",
    emailField: "Email",
    message: "Message",
    messagePlaceholder: "Your enquiry, context and timing...",
    namePlaceholder: "Full name",
    clubPlaceholder: "e.g. Sporting Director, FC...",
    emailPlaceholder: "you@club.com",
    send: "Send Enquiry",
    sending: "Sending...",
    required: "*",
    confidential: "All enquiries treated confidentially",
    missingTitle: "Missing fields",
    missingDesc: "Name, email and message are required.",
    sentTitle: "Enquiry sent",
    sentDesc:
      "Your message has been forwarded to the representation team. Expect a reply within 48h."
  },
  footer: {
    role: "Goalkeeper",
    tagline:
      "Official scouting portfolio. Distributed to verified professional football clubs, scouts and representatives.",
    navigate: "Navigate",
    representation: "Representation",
    copy: "All Rights Reserved",
    confidential: "Confidential · For Scouting Use Only"
  }
};

const fr = {
  nav: {
    profile: "Profil",
    video: "Vidéo",
    tactical: "Tactique",
    stats: "Statistiques",
    career: "Carrière",
    achievements: "Palmarès",
    gallery: "Galerie",
    contact: "Contact",
    dossier: "Dossier",
    downloadDossier: "Télécharger le dossier"
  },
  hero: {
    eyebrow: "Profil Scouting · 2025/26",
    subheadline:
      "Gardienne à l'Olympique de Marseille. Maîtresse de sa surface. Décisive dans les grands moments. Faite pour les plus grandes scènes.",
    watchReel: "Voir le highlight",
    viewProfile: "Voir le profil scouting",
    scroll: "Défiler",
    position: "Poste",
    nationality: "Nationalité",
    height: "Taille",
    currentClub: "Club actuel"
  },
  profile: {
    eyebrow: "01 · Profil Scouting",
    title: "La ",
    titleHighlight: "Joueuse",
    summary:
      "Emily Burns est une gardienne moderne au profil solide dans l'arrêt de tirs, avec une grande efficacité pour empêcher les buts et une présence défensive fiable. Elle combine d'excellents réflexes et un sens du placement remarquable, ce qui en fait une performeuse constante dans les situations sous pression.",
    labels: {
      dob: "Date de naissance",
      nationality: "Nationalité",
      height: "Taille",
      foot: "Pied préféré",
      club: "Club actuel",
      languages: "Langues",
      contract: "Contrat jusqu’à",
      status: "Statut"
    },
    values: {
      dob: "24 juillet 1997 · 28 ans",
      foot: "Droit",
      club: "Olympique de Marseille · France",
      languages: "Anglais, Français, Espagnol",
      contract: "Juin 2026",
      status: ""
    }
  },
  video: {
    eyebrow: "02 · Vidéo",
    title: "Sur le ",
    titleHighlight: "Terrain",
    description: "Reels scouting officiels — partagés en privé pour distribution aux clubs.",
    featured: "À la une · Version longue",
    featuredTitle: "Highlight Reel Officiel · Version Longue",
    featuredDescription:
      "Reel scouting étendu — arrêts, distribution, duels 1v1 et images de matchs.",
    shortTitle: "Highlight Reel · Version Courte",
    shortDuration: "Version courte",
    fullDuration: "Version complète",
    noteTitle: "Note scouting",
    noteText:
      "Deux versions disponibles : une version étendue pour une évaluation technique complète et une version courte pour une revue rapide.",
    nowPlaying: "En cours de lecture",
    tags: ["Arrêts", "Distribution", "1v1", "Jeu aérien", "Images de match"]
  },
  tactical: {
    eyebrow: "Rapport Scouting · Tactique",
    title: "Profil",
    titleHighlight: "Tactique",
    intro:
      "Emily Burns est une gardienne moderne au profil solide dans l'arrêt de tirs, avec une grande efficacité pour empêcher les buts et une présence défensive fiable. Elle combine d'excellents réflexes et un sens du placement remarquable, ce qui en fait une performeuse constante dans les situations sous pression.",
    blocks: [
      {
        tag: "01",
        title: "Style de jeu",
        paragraphs: [
          "Emily est une gardienne réactive et efficace qui excelle dans les situations exigeant une prise de décision rapide et une réponse immédiate. Son jeu est construit autour de l'arrêt de tirs, du placement et de la lecture précoce du danger."
        ]
      },
      {
        tag: "02",
        title: "Arrêt de tirs & réflexes",
        paragraphs: [
          "L'une de ses plus grandes qualités. Emily fait preuve d'une vitesse de réaction exceptionnelle, particulièrement dans les situations à courte distance et imprévisibles. Elle est très efficace contre les tirs ras de terre et les déflections, enchainant les arrêts grâce à ses réflexes et à sa coordination.",
          "Son efficacité aux arrêts se situe au-dessus de la moyenne dans plusieurs championnats de haut niveau, et elle performe au-dessus de 75 % des gardiennes en arrêts par 90 minutes dans les compétitions de premier plan."
        ]
      },
      {
        tag: "03",
        title: "Impact sur les buts évités",
        paragraphs: [
          "Emily est une gardienne à fort impact en termes de buts évités. Elle performe régulièrement au-dessus de la moyenne en buts évités par 90 minutes, démontrant sa capacité à influencer directement le résultat des matchs.",
          "Ses interventions ont été décisives, contribuant significativement aux résultats de l'équipe, avec notamment des arrêts clés dans les moments décisifs."
        ]
      },
      {
        tag: "04",
        title: "Sortie de but",
        paragraphs: [
          "Elle montre un très bon sens du timing lorsqu'elle quitte sa ligne, attaquant le ballon avec détermination et réduisant efficacement l'espace disponible pour l'attaquant. Sa capacité à étendre son corps et à réagir rapidement lui permet de remporter les 1v1."
        ]
      },
      {
        tag: "05",
        title: "Jeu aérien & contrôle de la surface",
        paragraphs: [
          "Emily affiche une prestation solide dans les duels aériens, avec un taux de réussite de 72 %, démontrant sa fiabilité sur les centres et les coups de pied arrêtés."
        ]
      },
      {
        tag: "06",
        title: "Distribution & relance",
        paragraphs: [
          "Son jeu au pied est techniquement fiable et sa prise de décision sous pression est solide. Elle perd rarement ses passes et montre une bonne conscience avant de recevoir le ballon."
        ]
      },
      {
        tag: "07",
        title: "Mentalité & régularité",
        paragraphs: [
          "Emily a prouvé qu'elle était une joueuse fiable et constante, capable de maintenir un haut niveau sur la durée. Elle a réalisé des arrêts cruciaux dans des moments clés, y compris des interventions de fin de match qui ont directement apporté des points à son équipe.",
          "Elle joue avec calme, concentration et une forte compréhension du jeu, maintenant sa performance sous pression."
        ]
      }
    ],
    competitive: {
      title: "Niveau compétitif",
      note:
        "Elle se trouve actuellement à un stade de maturité optimal pour le poste, combinant expérience et potentiel de développement continu.",
      bullets: [
        "Profil Top 10 de gardienne dans les compétitions de haut niveau",
        "Au-dessus de la moyenne sur les indicateurs de performance clés",
        "Se compare favorablement aux gardiennes de WSL, Liga F, NWSL et Bundesliga"
      ]
    },
    summary: {
      label: "Synthèse du profil",
      p1:
        "Une gardienne à haut rendement avec une capacité d'élite à arrêter les tirs, un impact fort dans la prévention des buts, et une présence défensive solide. Particulièrement adaptée aux équipes qui exigent de la fiabilité sous pression et une gardienne capable de sécuriser les résultats par des arrêts décisifs.",
      p2:
        "Son profil convient parfaitement aux environnements compétitifs et aux équipes qui privilégient la solidité défensive, tout en offrant encore une marge de progression dans les systèmes basés sur la possession."
    }
  },
  stats: {
    eyebrow: "03 · Performance",
    title: "Les chiffres ",
    titleHighlight: "parlent",
    season: "2025/26 · Arkema Première Ligue",
    labels: {
      matches: "Matchs joués",
      minutes: "Minutes",
      saves: "% Arrêts",
      aerials: "Duels aériens gagnés",
      reflex: "Arrêts réflexes",
      passAcc: "Réussite des passes",
      longPass: "Réussite longues passes",
      gPrevented: "Buts évités / 90",
      totalActions: "Actions totales réussies",
      cleanSheets: "Clean sheets"
    },
    rankingsTitle: "Benchmark par championnat · Wyscout",
    rankingsNote: "Classement comparatif des gardiennes dans les principaux championnats et au niveau mondial."
  },
  career: {
    eyebrow: "04 · Carrière",
    title: "Le ",
    titleHighlight: "Parcours",
    intro: "Carrière senior en France, en Espagne et au Canada.",
    matchesLabel: "Matchs",
    leagueLabel: "Championnat"
  },
  achievements: {
    eyebrow: "05 · Palmarès",
    title: "Titres & ",
    titleHighlight: "Impact",
    intro: "Reconnaissance et contributions décisives sur les dernières saisons."
  },
  gallery: {
    eyebrow: "06 · Galerie",
    title: "En ",
    titleHighlight: "Image"
  },
  dossier: {
    eyebrow: "07 · Dossier",
    title: "Le ",
    titleHighlight: "Dossier Complet",
    intro:
      "Un dossier scouting complet au format PDF, prêt à être partagé aux directeurs sportifs, scouts et analystes. Inclut le profil statistique complet, l'analyse tactique, le benchmark par championnat et l'historique de carrière.",
    download: "Télécharger le dossier PDF",
    request: "Demander un accès privé",
    confidential: "Confidentiel · Distribué sur demande",
    includedTitle: "Contenu du dossier",
    included: [
      "Profil scouting et biographie complets",
      "Profil statistique avec données Wyscout",
      "Attributs tactiques & techniques",
      "Benchmark par championnat (ENG, ESP, USA, Top 20 Mondial)",
      "Historique de carrière · 6 dernières saisons",
      "Palmarès et contributions décisives"
    ],
    format: "Format",
    updated: "Mis à jour",
    updatedValue: "2025/26"
  },
  contact: {
    eyebrow: "08 · Contact",
    title: "Prises",
    titleHighlight: "de contact",
    intro:
      "Pour toute demande de transfert, requête scouting ou information complémentaire — tous les messages sont traités directement par la représentation officielle FIFA.",
    email: "Email",
    phone: "Téléphone / WhatsApp",
    representation: "Représentation",
    basedIn: "Basé",
    instagram: "Instagram",
    fullName: "Votre nom",
    club: "Club / Organisation",
    emailField: "Email",
    message: "Message",
    messagePlaceholder: "Votre demande, contexte et délais...",
    namePlaceholder: "Nom complet",
    clubPlaceholder: "ex. Directeur sportif, FC...",
    emailPlaceholder: "vous@club.com",
    send: "Envoyer la demande",
    sending: "Envoi...",
    required: "*",
    confidential: "Toutes les demandes sont traitées avec confidentialité",
    missingTitle: "Champs manquants",
    missingDesc: "Nom, email et message sont obligatoires.",
    sentTitle: "Demande envoyée",
    sentDesc:
      "Votre message a été transmis à l'équipe de représentation. Réponse attendue sous 48h."
  },
  footer: {
    role: "Gardienne",
    tagline:
      "Portfolio scouting officiel. Distribué aux clubs professionnels vérifiés, scouts et représentants.",
    navigate: "Navigation",
    representation: "Représentation",
    copy: "Tous droits réservés",
    confidential: "Confidentiel · Usage scouting uniquement"
  }
};

const es = {
  nav: {
    profile: "Perfil",
    video: "Vídeo",
    tactical: "Táctica",
    stats: "Estadísticas",
    career: "Trayectoria",
    achievements: "Palmarés",
    gallery: "Galería",
    contact: "Contacto",
    dossier: "Dossier",
    downloadDossier: "Descargar dossier"
  },
  hero: {
    eyebrow: "Perfil Scouting · 2025/26",
    subheadline:
      "Portera del Olympique de Marseille. Domina el área. Decide el momento. Preparada para los más grandes escenarios.",
    watchReel: "Ver highlight reel",
    viewProfile: "Ver perfil scouting",
    scroll: "Desplaza",
    position: "Posición",
    nationality: "Nacionalidad",
    height: "Altura",
    currentClub: "Club actual"
  },
  profile: {
    eyebrow: "01 · Perfil Scouting",
    title: "La ",
    titleHighlight: "Jugadora",
    summary:
      "Emily Burns es una portera moderna con un perfil sólido en el stop de disparos, alta eficiencia en la prevención de goles y una presencia defensiva fiable. Combina excelentes reflejos con una fuerte lectura del juego, lo que la convierte en una portera constante en escenarios de alta presión.",
    labels: {
      dob: "Fecha de nacimiento",
      nationality: "Nacionalidad",
      height: "Altura",
      foot: "Pie preferido",
      club: "Club actual",
      languages: "Idiomas",
      contract: "Contrato hasta",
      status: "Estado"
    },
    values: {
      dob: "24 de julio de 1997 · 28 años",
      foot: "Derecho",
      club: "Olympique de Marseille · Francia",
      languages: "Inglés, Francés, Español",
      contract: "Junio 2026",
      status: ""
    }
  },
  video: {
    eyebrow: "02 · Vídeo",
    title: "En el ",
    titleHighlight: "Campo",
    description: "Reels de scouting oficiales — compartidos en privado para distribución a clubes.",
    featured: "Destacado · Versión larga",
    featuredTitle: "Highlight Reel Oficial · Versión Larga",
    featuredDescription:
      "Reel scouting extendido — paradas, distribución, mano a mano e imágenes de partido.",
    shortTitle: "Highlight Reel · Versión Corta",
    shortDuration: "Versión corta",
    fullDuration: "Versión completa",
    noteTitle: "Nota scouting",
    noteText:
      "Dos versiones disponibles: una versión extendida para una evaluación técnica completa y un corte breve para revisión rápida.",
    nowPlaying: "Reproduciendo",
    tags: ["Paradas", "Distribución", "1v1", "Juego aéreo", "Imágenes de partido"]
  },
  tactical: {
    eyebrow: "Informe Scouting · Táctico",
    title: "Perfil",
    titleHighlight: "Táctico",
    intro:
      "Emily Burns es una portera moderna con un perfil sólido en el stop de disparos, alta eficiencia en la prevención de goles y una presencia defensiva fiable. Combina excelentes reflejos con una fuerte lectura del juego, lo que la convierte en una portera constante en escenarios de alta presión.",
    blocks: [
      {
        tag: "01",
        title: "Estilo de juego",
        paragraphs: [
          "Emily es una portera reactiva y eficiente que destaca en situaciones que requieren toma de decisiones rápida y respuesta inmediata. Su juego se construye alrededor del stop de disparos, el posicionamiento y la lectura temprana del peligro."
        ]
      },
      {
        tag: "02",
        title: "Stop de disparos y reflejos",
        paragraphs: [
          "Una de sus fortalezas más destacadas. Emily muestra una velocidad de reacción excepcional, especialmente en situaciones a corta distancia e impredecibles. Es muy efectiva contra disparos rasos y desvíos, encadenando paradas gracias a sus reflejos y coordinación corporal.",
          "Su eficiencia en paradas se sitúa por encima de la media en varias ligas top, y rinde por encima del 75 % de porteras en paradas por 90 minutos en competiciones de alto nivel."
        ]
      },
      {
        tag: "03",
        title: "Impacto en goles evitados",
        paragraphs: [
          "Emily es una portera de alto impacto en términos de goles evitados. Rinde consistentemente por encima de la media en goles evitados por 90 minutos, demostrando su capacidad para influir directamente en el resultado de los partidos.",
          "Sus intervenciones han sido decisivas, contribuyendo significativamente a los resultados del equipo, incluyendo paradas clave en momentos decisivos."
        ]
      },
      {
        tag: "04",
        title: "Salidas del área",
        paragraphs: [
          "Muestra un timing muy bueno al salir de la línea, atacando el balón con determinación y reduciendo eficazmente el espacio disponible para el atacante. Su capacidad para extender el cuerpo y reaccionar rápidamente le permite ganar los mano a mano."
        ]
      },
      {
        tag: "05",
        title: "Juego aéreo y control del área",
        paragraphs: [
          "Emily muestra un rendimiento sólido en los duelos aéreos, con un 72 % de éxito, mostrando fiabilidad en centros y situaciones de balón parado."
        ]
      },
      {
        tag: "06",
        title: "Distribución y salida",
        paragraphs: [
          "Su juego de pies es técnicamente fiable y la toma de decisiones bajo presión es sólida. Rara vez falla los pases y muestra buena consciencia antes de recibir el balón."
        ]
      },
      {
        tag: "07",
        title: "Mentalidad y consistencia",
        paragraphs: [
          "Emily ha demostrado ser una jugadora fiable y consistente, capaz de mantener un alto nivel en el tiempo. Ha realizado paradas cruciales en momentos clave, incluidas intervenciones de final de partido que han aportado puntos directos a su equipo.",
          "Juega con calma, concentración y una fuerte comprensión del juego, manteniendo su rendimiento bajo presión."
        ]
      }
    ],
    competitive: {
      title: "Nivel competitivo",
      note:
        "Se encuentra actualmente en una fase óptima de madurez para el puesto, combinando experiencia con potencial de desarrollo continuo.",
      bullets: [
        "Perfil Top 10 de portera en competiciones de alto nivel",
        "Por encima de la media en métricas clave de rendimiento",
        "Compite favorablemente con porteras de WSL, Liga F, NWSL y Bundesliga"
      ]
    },
    summary: {
      label: "Síntesis del perfil",
      p1:
        "Una portera de alto rendimiento con habilidad de élite en el stop de disparos, fuerte impacto en la prevención de goles y sólida presencia defensiva. Especialmente indicada para equipos que requieren fiabilidad bajo presión y una portera capaz de asegurar resultados mediante paradas decisivas.",
      p2:
        "Su perfil encaja bien en entornos competitivos y equipos que priorizan la solidez defensiva, ofreciendo aún margen de crecimiento en sistemas basados en la posesión."
    }
  },
  stats: {
    eyebrow: "03 · Rendimiento",
    title: "Los números ",
    titleHighlight: "hablan",
    season: "2025/26 · Arkema Première Ligue",
    labels: {
      matches: "Partidos jugados",
      minutes: "Minutos",
      saves: "% Paradas",
      aerials: "Duelos aéreos ganados",
      reflex: "Paradas reflejas",
      passAcc: "Precisión de pase",
      longPass: "Precisión pase largo",
      gPrevented: "Goles evitados / 90",
      totalActions: "Acciones totales acertadas",
      cleanSheets: "Porterías a cero"
    },
    rankingsTitle: "Benchmark por liga · Wyscout",
    rankingsNote: "Ranking comparativo de porteras en las principales ligas y a nivel mundial."
  },
  career: {
    eyebrow: "04 · Trayectoria",
    title: "El ",
    titleHighlight: "Recorrido",
    intro: "Trayectoria sénior por Francia, España y Canadá.",
    matchesLabel: "Partidos",
    leagueLabel: "Liga"
  },
  achievements: {
    eyebrow: "05 · Palmarés",
    title: "Premios e ",
    titleHighlight: "Impacto",
    intro: "Reconocimientos y contribuciones decisivas en las últimas temporadas."
  },
  gallery: {
    eyebrow: "06 · Galería",
    title: "En ",
    titleHighlight: "Imagen"
  },
  dossier: {
    eyebrow: "07 · Dossier",
    title: "El ",
    titleHighlight: "Dossier Completo",
    intro:
      "Un dossier scouting completo en PDF, listo para compartir con directores deportivos, scouts y analistas. Incluye perfil estadístico completo, análisis táctico, benchmark por liga e historial de trayectoria.",
    download: "Descargar dossier PDF",
    request: "Solicitar acceso privado",
    confidential: "Confidencial · Distribuido bajo petición",
    includedTitle: "Contenido del dossier",
    included: [
      "Perfil scouting y biografía completos",
      "Perfil estadístico con datos Wyscout",
      "Atributos tácticos y técnicos",
      "Benchmark por liga (ENG, ESP, USA, Top 20 Mundial)",
      "Trayectoria · últimas 6 temporadas",
      "Palmarés y contribuciones decisivas"
    ],
    format: "Formato",
    updated: "Actualizado",
    updatedValue: "2025/26"
  },
  contact: {
    eyebrow: "08 · Contacto",
    title: "Contacto",
    titleHighlight: "directo",
    intro:
      "Para consultas de traspaso, solicitudes de scouting o información adicional — todos los mensajes los gestiona directamente la representación oficial FIFA.",
    email: "Email",
    phone: "Teléfono / WhatsApp",
    representation: "Representación",
    basedIn: "Ubicación",
    instagram: "Instagram",
    fullName: "Tu nombre",
    club: "Club / Organización",
    emailField: "Email",
    message: "Mensaje",
    messagePlaceholder: "Tu consulta, contexto y timing...",
    namePlaceholder: "Nombre completo",
    clubPlaceholder: "ej. Director deportivo, FC...",
    emailPlaceholder: "tu@club.com",
    send: "Enviar consulta",
    sending: "Enviando...",
    required: "*",
    confidential: "Todas las consultas se tratan de forma confidencial",
    missingTitle: "Campos incompletos",
    missingDesc: "Nombre, email y mensaje son obligatorios.",
    sentTitle: "Consulta enviada",
    sentDesc:
      "Tu mensaje ha sido reenviado al equipo de representación. Respuesta esperada en menos de 48h."
  },
  footer: {
    role: "Portera",
    tagline:
      "Portfolio scouting oficial. Distribuido a clubes profesionales verificados, scouts y representantes.",
    navigate: "Navegación",
    representation: "Representación",
    copy: "Todos los derechos reservados",
    confidential: "Confidencial · Uso exclusivo scouting"
  }
};

export const translations = { en, fr, es };
